<!--
  Created 20.07.2024
  Component used for editing the options of a section in a proposal.
-->
<template>
  <div
    class="group pointer-events-none absolute left-4 top-4 flex cursor-pointer flex-col space-y-6
                rounded-lg border border-transparent p-1 pr-2.5 text-sm font-medium text-gray-800
                opacity-98 hover:border-gray-700 hover:bg-neutral-400 hover:text-gray-900"
    :class="{ '!left-2 !top-2 !border-gray-700 bg-neutral-400 !pr-5 pb-4 pl-3 pt-3 !text-gray-900 shadow-2xl shadow-neutral-600' : adjustmentActive }">
    <div
      class="pointer-events-auto flex items-center space-x-1"
      :class="{ 'space-x-2': adjustmentActive }"
      @click="adjustmentActive=!adjustmentActive">
      <AdjustmentsVerticalIcon
        class="h-8 w-8"
        aria-hidden="true" />
      <div
        class="hidden group-hover:block"
        :class="{ '!block text-lg': adjustmentActive }">
        <span v-if="!adjustmentActive">
          Click to open section options
        </span>
        <span v-else>
          Edit section options <small>(click here to close)</small>
        </span>
      </div>
    </div>
    <div
      v-if="adjustmentActive"
      class="pointer-events-auto ml-10 flex items-center space-x-2 pb-4">
      <ShortToggle
        color-enabled="bg-neutral-800"
        :enabled="store.proposal.data.sections[props.section].visible"
        @update:enabled="toggleVisibility" />
      <span class="pb-0.5 text-sm">Section is <i>{{ store.proposal.data.sections[props.section].visible ? 'visible' : 'hidden' }}</i></span>
      <span
        class="invisible -mt-1 text-lg font-bold text-red-500"
        :class="{'!visible':changed}">*</span>
    </div>
  </div>
</template>

<script setup>
import {
  computed,
  ref, watch,
} from 'vue';
import ShortToggle from '@/Components/Forms/ShortToggle.vue';
import { AdjustmentsVerticalIcon } from '@heroicons/vue/24/outline';
import { useProposalStore } from '@/Stores/Proposal';

const props = defineProps({
  /**
   * The section to which the options belong.
   * This should be the key of the section in the proposal data.
   */
  section: {
    type: String,
    required: true,
  },
});

const adjustmentActive = ref(false);
const changed = ref(false);

const store = useProposalStore();
const savedChangesProposal = computed(() => !store.unsavedChanges.changes.proposal);

/**
 * Toggles the visibility of the program section.
 */
function toggleVisibility() {
  changed.value = true;

  // change the visibility of the section
  store.proposal.data.sections[props.section].visible = !store.proposal.data.sections[props.section].visible;

  // Find the index of the corresponding navItem by sectionKey
  // noinspection JSUnresolvedReference (as findIndex actually works here)
  const navItemIndex = store.navItems.findIndex((item) => item.section === props.section);
  // Check if the navItem exists
  if (navItemIndex !== -1) {
    // Update the navItem's visibility reactively
    store.navItems[navItemIndex].visible = store.proposal.data.sections[props.section].visible;
    if (props.section === 'prices') {
      store.navItems[navItemIndex + 1].visible = !store.navItems[navItemIndex].visible;
    }
  }

  // wait for user to stop toggling before saving to database
  store.setSaveTimer(
    () => {
      store.unsavedChanges.changes.proposal = true;
    },
    1500,
  );
}

watch(savedChangesProposal, (value) => {
  if (value === true) {
    changed.value = false;
  }
});

</script>
